import { useMemo, useState } from "react"

import type { Offer } from "@/api/hooks/offers"
import {
  useKlowGoogleTemplates,
  useKlowPixels,
  useKlowRsocTemplates,
  useUpdateKlowOffer,
  type VerticalOption,
} from "@/api/hooks/klow"
import { ApiError } from "@/api/client"
import { useAuth } from "@/auth/AuthProvider"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { defaultVerticalDisplayName } from "@/lib/verticalCatalog"
import { cn } from "@/lib/utils"

interface KlowOfferEditModalProps {
  open: boolean
  offer: Offer | null
  verticalOptions: VerticalOption[]
  isLoadingVerticals?: boolean
  onClose: () => void
  onSuccess: () => void
}

export function KlowOfferEditModal({
  open,
  offer,
  verticalOptions,
  isLoadingVerticals = false,
  onClose,
  onSuccess,
}: KlowOfferEditModalProps) {
  const { user } = useAuth()
  const updateMutation = useUpdateKlowOffer()
  const [offerName, setOfferName] = useState("")
  const [verticalSlug, setVerticalSlug] = useState("")
  const [rsocTemplateId, setRsocTemplateId] = useState("")
  const [googleTemplateId, setGoogleTemplateId] = useState("")
  const [sqs, setSqs] = useState("")
  const [pixelId, setPixelId] = useState("")
  const [error, setError] = useState<string | null>(null)

  const rsocTemplatesQuery = useKlowRsocTemplates(open)
  const googleTemplatesQuery = useKlowGoogleTemplates(open)
  const pixelsQuery = useKlowPixels({ enabled: open, userId: user?.id, mine: true })

  const articleSlug = useMemo(
    () => offer?.klow_article_slug || "",
    [offer?.klow_article_slug],
  )

  // Re-seed the form whenever the modal opens or the edited offer changes
  // (adjusting state during render instead of in an effect).
  const [seededOpen, setSeededOpen] = useState(false)
  const [seededOffer, setSeededOffer] = useState<Offer | null>(null)
  if (open !== seededOpen || offer !== seededOffer) {
    setSeededOpen(open)
    setSeededOffer(offer)
    if (open && offer) {
      setOfferName(offer.name)
      setVerticalSlug((offer.vertical_slug || "").trim())
      setRsocTemplateId(offer.klow_rsoc_template_id || "")
      setGoogleTemplateId(offer.klow_google_template_id || "")
      setSqs(offer.klow_suggested_keywords || "")
      setPixelId(offer.klow_pixel_id || "")
      setError(null)
    }
  }

  const rsocTemplateOptions = useMemo(
    () =>
      (rsocTemplatesQuery.data?.data ?? []).map((t) => ({
        value: String(t.ct),
        label: t.NAME ? `${t.NAME} (${t.ct})` : String(t.ct),
      })),
    [rsocTemplatesQuery.data?.data],
  )

  const googleTemplateOptions = useMemo(
    () =>
      (googleTemplatesQuery.data?.data ?? []).map((t) => ({
        value: String(t.style_id),
        label: `Style ${t.style_id}`,
      })),
    [googleTemplatesQuery.data?.data],
  )

  const pixelSelectOptions = useMemo(() => {
    const raw = pixelsQuery.data?.data
    if (!Array.isArray(raw)) return []
    return raw.map((p) => ({
      value: String(p.rsoc_spid),
      label: p.label || `${p.name} (${p.rsoc_spid})`,
    }))
  }, [pixelsQuery.data?.data])

  const handleSave = async () => {
    if (!offer) return
    if (!offerName.trim()) {
      setError("Offer name is required")
      return
    }
    if (!rsocTemplateId.trim()) {
      setError("Article template (ct) is required")
      return
    }
    if (!googleTemplateId.trim()) {
      setError("KW template (stid) is required")
      return
    }
    if (!pixelId.trim()) {
      setError("Pixel (spid) is required")
      return
    }

    const vertical = verticalOptions.find((v) => v.slug === verticalSlug)
    try {
      await updateMutation.mutateAsync({
        offerId: offer.id,
        body: {
          name: offerName.trim(),
          vertical_slug: vertical?.slug || null,
          vertical_name:
            vertical?.name ||
            (verticalSlug ? defaultVerticalDisplayName(verticalSlug) : null),
          rsoc_template_id: rsocTemplateId.trim(),
          google_template_id: googleTemplateId.trim(),
          suggested_keywords: sqs.trim(),
          pixel_id: pixelId.trim(),
        },
      })
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save offer")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[#4a4a4a] bg-[#2a2a2a] text-[#e0e0e0]">
        <DialogHeader>
          <DialogTitle>Edit KLOW offer</DialogTitle>
        </DialogHeader>

        {isLoadingVerticals ? (
          <p className="py-8 text-center text-sm text-[#888]">Loading verticals…</p>
        ) : (
        <div className="space-y-4">
          <div>
            <Label htmlFor="klow-edit-name">Offer name</Label>
            <Input
              id="klow-edit-name"
              value={offerName}
              onChange={(e) => {
                setOfferName(e.target.value)
                setError(null)
              }}
              className="border-[#555] bg-[#3a3a3a]"
            />
          </div>

          <div>
            <Label>Vertical</Label>
            <Select
              value={verticalSlug || undefined}
              onValueChange={(value) => {
                setVerticalSlug(value)
                setError(null)
              }}
            >
              <SelectTrigger className="border-[#555] bg-[#3a3a3a]">
                <SelectValue placeholder="Select vertical" />
              </SelectTrigger>
              <SelectContent
                className="border-[#555] bg-[#2a2a2a]"
                position="popper"
                sideOffset={4}
              >
                {verticalOptions.map((option) => (
                  <SelectItem key={option.slug} value={option.slug}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Article</Label>
            <div className="rounded-md border border-[#555] bg-[#333] px-3 py-2 text-sm text-[#bbb]">
              {articleSlug || "Unknown article"}
            </div>
            <p className="mt-1 text-xs text-[#888]">
              Article cannot be changed when editing an offer.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Article template (ct)</Label>
              <Select
                value={rsocTemplateId || undefined}
                onValueChange={(value) => {
                  setRsocTemplateId(value)
                  setError(null)
                }}
              >
                <SelectTrigger className="border-[#555] bg-[#3a3a3a]">
                  <SelectValue placeholder="Pick layout template" />
                </SelectTrigger>
                <SelectContent
                  className="border-[#555] bg-[#2a2a2a]"
                  position="popper"
                  sideOffset={4}
                >
                  {rsocTemplateOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>KW template (stid)</Label>
              <Select
                value={googleTemplateId || undefined}
                onValueChange={(value) => {
                  setGoogleTemplateId(value)
                  setError(null)
                }}
              >
                <SelectTrigger className="border-[#555] bg-[#3a3a3a]">
                  <SelectValue placeholder="Pick KW template" />
                </SelectTrigger>
                <SelectContent
                  className="border-[#555] bg-[#2a2a2a]"
                  position="popper"
                  sideOffset={4}
                >
                  {googleTemplateOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="klow-edit-sqs">Suggested keywords (sqs)</Label>
            <Input
              id="klow-edit-sqs"
              value={sqs}
              onChange={(e) => setSqs(e.target.value)}
              placeholder="Optional"
              className="border-[#555] bg-[#3a3a3a]"
            />
          </div>

          <div>
            <Label>Pixel (spid)</Label>
            <Select
              value={pixelId || undefined}
              onValueChange={(value) => {
                setPixelId(value)
                setError(null)
              }}
            >
              <SelectTrigger className="border-[#555] bg-[#3a3a3a]">
                <SelectValue placeholder="Select pixel" />
              </SelectTrigger>
              <SelectContent
                className="border-[#555] bg-[#2a2a2a]"
                position="popper"
                sideOffset={4}
              >
                {pixelSelectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateMutation.isPending}
            className="border-[#555] bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={updateMutation.isPending || isLoadingVerticals}
            className={cn("bg-[#7488e8] text-white hover:bg-[#6677dd]")}
          >
            {updateMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
